import { checkoutSchema } from "@/schemas/checkoutSchema";
import React, { useRef } from "react";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { Button } from "@/components/ui/button";

import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import ShippingInformation from "./shippingInformation/ShippingInformation";
import PaymentMethod from "./paymentMethod/PaymentMethod";
import { useAppSelector } from "@/store/hooks";
import useCreateCustomer from "@/hooks/stripe/useCreateCustomer";
import usePaymentIntent from "@/hooks/stripe/usePaymentIntent";
import useAttachPaymentMethod from "@/hooks/stripe/useAttachPaymentMethod";
import { UserBilling, UserInfo } from "@/types/AppTypes";


type CheckoutFormValues = z.infer<typeof checkoutSchema>;

const CheckoutForm = ({
  handlePurchase, 
  isSubmitting, 
  totalAmount,
  userExistingStripePaymentMethods,
  user,
  userBilling,
  existingShippingAddress,
  saveShippingAddress,
  onShippingAddressNameSave,
  onExistingAddressSelection, // Add this new prop

}: {
  handlePurchase: (data: CheckoutFormValues) => void, 
  isSubmitting: boolean, 
  totalAmount: number,
  userExistingStripePaymentMethods: any[];
  user: UserInfo | null;
  userBilling: UserBilling | undefined;
  existingShippingAddress: any;
  saveShippingAddress: (value: boolean) => void;
  onShippingAddressNameSave: (name: string) => void;
  onExistingAddressSelection?: (addressName: string) => void; // Add this
}) => {
  const { stylistInfo } = useAppSelector((state) => state.auth);
  const [paymentMethod, setPaymentMethod] = useState<"card" | "paypal" | "cash">("card");
  const paymentMethodRef = useRef<any>(null);

  const {
    attachPaymentMethod,
    loading: attachLoading,
    error: attachError,
  } = useAttachPaymentMethod();

  // Stripe hooks
  const { createCustomer, loading: customerLoading, error: customerError } = useCreateCustomer();
  const { createPaymentIntent, loading: paymentLoading } = usePaymentIntent();

  const form = useForm<CheckoutFormValues>({
    resolver: zodResolver(checkoutSchema),
    defaultValues: {
      firstName: user?.first_name || "",
      lastName: user?.last_name || "",
      email: user?.email || "",
      suite: stylistInfo?.suite || "",
      phone: stylistInfo?.phone || "",
      street: stylistInfo?.street ?? "",
      city: stylistInfo?.city||"",
      state: stylistInfo?.state || "",
      zipCode: stylistInfo?.postal_code || "",
      country: stylistInfo?.country || "",
      paymentMethod: "card",
      stripePaymentMethodId: "",
    },
  });

  const handlePaymentMethodChange = (method: "card" | "paypal" | "cash") => {
    setPaymentMethod(method);
    form.setValue("paymentMethod", method);
  };

  const handlePaymentMethodCreate = (paymentMethodId: string) => {
    form.setValue("stripePaymentMethodId", paymentMethodId);
  };

  // const onSubmit = async (data: CheckoutFormValues) => {
  //   try {
  //     if (data.paymentMethod === "card") {
    
  //       toast.loading("Processing payment...");
        
  //       // 1. Create customer
  //       const customer = await createCustomer({
  //         name: `${data.firstName} ${data.lastName}`,
  //         email: data.email,
  //       });

  //       if (!customer) {
  //         toast.dismiss();
  //         toast.error("Failed to create customer account");
  //         return;
  //       }

  //       // 2. Create payment method
  //       const billingDetails = {
  //         name: `${data.firstName} ${data.lastName}`,
  //         address: {
  //           line1: data.street,
  //           city: data.city,
  //           state: data.state,
  //           postal_code: data.zipCode,
  //           country: data.country, 
  //         },
  //       };

  //       const paymentMethod = await paymentMethodRef.current?.createPaymentMethod(billingDetails);
        
  //       if (!paymentMethod) {
  //         toast.dismiss();
  //         toast.error("Failed to process payment method");
  //         return;
  //       }

  //       if (paymentMethodId && customerId) {
  //         const attachResult = await attachPaymentMethod({
  //           customerId: customerId,
  //           paymentMethodId: paymentMethodId,
  //         });

  //         // CRITICAL: Check both result and error state
  //         if (!attachResult || attachError) {
  //           toast.dismiss();
  //           console.error("Attach payment method error:", attachError);
  //           throw new Error("Failed to attach payment method");
  //         }
  //       }

  //       const paymentIntentResult = await createPaymentIntent({
  //         amount: Math.round(totalAmount * 100),
  //         currency: 'usd',
  //         customer: customer.id,
  //         payment_method: paymentMethod.id,
  //         off_session: true,
  //         confirm: true,
  //         description: 'Order payment',
  //       });

  //       if (!paymentIntentResult || paymentIntentResult.status !== 'succeeded') {
  //         toast.dismiss();
  //         toast.error("Payment failed", {
  //           description: "Please check your card details and try again"
  //         });
  //         return;
  //       }

  //       toast.dismiss();
  //       toast.success("Payment processed successfully!");

  //       // Store payment details
  //       data.stripePaymentMethodId = paymentMethod.id;
  //       data.paymentIntentId = paymentIntentResult.id;
  //     }

  //     if (handlePurchase) {
  //       await handlePurchase(data);
  //     }
  //   } catch (error :any) {
  //     toast.dismiss();
  //     console.error("Error during form submission:", error);
  
  //     if (error.message?.includes('card')) {
  //       toast.error("Card payment failed", {
  //         description: "Please check your card details and try again"
  //       });
  //     } else if (error.message?.includes('customer')) {
  //       toast.error("Account creation failed", {
  //         description: "Please try again or contact support"
  //       });
  //     } else {
  //       toast.error("Payment processing failed", {
  //         description: "Please try again or use a different payment method"
  //       });
  //     }
  //   }
  // };

  const onSubmit = async (data: CheckoutFormValues) => {
    try {
      if (data.paymentMethod === "card") {
        toast.loading("Processing payment...");
        let customerId = userBilling?.customer_id || null;

        // Step 1: Create customer if needed
        if (!customerId) {
          const customer = await createCustomer({
            name: `${data.firstName} ${data.lastName}`,
            email: data.email,
          });

          if (!customer) {
            toast.dismiss();

            console.error("Customer creation error:", customerError);
            throw new Error("Failed to create customer");
          }
          customerId = customer.id;
        }

        let paymentMethodId = data.stripePaymentMethodId;

        // Step 2: Create payment method ONLY if no existing payment method is selected
        if (!data.stripePaymentMethodId || data.stripePaymentMethodId === "") {
          const billingDetails = {
            name: `${data.firstName} ${data.lastName}`,
            address: {
              line1: (data.suite ? `, ${data.suite}` : "") + data.street,
              city: data.city,
              state: data.state,
              postal_code: data.zipCode,
              country: data.country,
            },
          };

          const paymentMethod = await paymentMethodRef.current?.createPaymentMethod(billingDetails);

          if (!paymentMethod) {
            toast.dismiss();
            console.error("Payment method creation error:", paymentMethod.error);
            throw new Error("Failed to create payment_method");
          }

          paymentMethodId = paymentMethod.id;
        }

        // Step 3: Attach payment method - CRITICAL VALIDATION ADDED
        if (paymentMethodId && customerId) {
          const attachResult = await attachPaymentMethod({
            customerId: customerId,
            paymentMethodId: paymentMethodId,
          });

          // CRITICAL: Check both result and error state
          if (!attachResult || attachError) {
            toast.dismiss();
            console.error("Attach payment method error:", attachError);
            throw new Error("Failed to attach payment method");
          }
        }

        // Step 4: Create Payment Intent - CRITICAL VALIDATION ADDED

        const paymentIntentResult = await createPaymentIntent({
          amount: Math.round(totalAmount * 100),
          currency: "usd",
          customer: customerId,
          payment_method: paymentMethodId ?? "",
          off_session: true,
          confirm: true,
          description: "Order payment",
        });

        // CRITICAL: Check both result and error state
        if (!paymentIntentResult || paymentIntentResult.status !== "succeeded") {
          toast.dismiss();
          console.error("Payment Intent creation error:", paymentIntentResult);
          throw new Error("Failed to create Payment Intent");
        }

        toast.dismiss();
        toast.success("Payment processed successfully!");

        // Store payment details - CRITICAL: Add subscription ID
        data.stripePaymentMethodId = paymentMethodId || "";
        data.customerId = customerId || "";
      }

      // CRITICAL: Only proceed to GraphQL operations if ALL Stripe operations succeeded
      if (handlePurchase) {
        await handlePurchase(data);
      }
    } catch (error: any) {
      toast.dismiss();
      console.error("Error during form submission:", error);

      // CRITICAL: More specific error handling
      if (error.message?.includes("card") || error.message?.includes("payment_method")) {
        toast.error("Card payment failed", {
          description: "Please check your card details and try again",
        });
      } else if (error.message?.includes("customer")) {
        toast.error("Account creation failed", {
          description: "Please try again or contact support",
        });
      } else if (error.message?.includes("intent")) {
        toast.error("Payment Intent creation failed", {
          description: "Please try again or contact support",
        });
      } else if (error.message?.includes("attach")) {
        toast.error("Payment method attachment failed", {
          description: "Please try again or contact support",
        });
      } else {
        toast.error("Payment processing failed", {
          description: "Please try again or use a different payment method",
        });
      }
    }
  };

  const handleStripeExistingPaymentMethodSelect = (paymentMethodId: string) => {
    form.setValue("stripePaymentMethodId", paymentMethodId || "");
  };
  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div className="space-y-6">
        <ShippingInformation 
          form={form} 
          existingShippingAddress={existingShippingAddress} 
          saveShippingAddress={saveShippingAddress}
          onShippingAddressNameSave={onShippingAddressNameSave}
          onExistingAddressSelection={onExistingAddressSelection}
        />
        <PaymentMethod
          ref={paymentMethodRef}
          form={form}
          selectedMethod={paymentMethod}
          onMethodChange={handlePaymentMethodChange}
          onPaymentMethodCreate={handlePaymentMethodCreate}
          existingStripePaymentMethods={userExistingStripePaymentMethods}
          onStripeExistingPaymentMethodSelect={handleStripeExistingPaymentMethodSelect}
        />

        <div className="flex justify-end mt-8">
          <Button
            type="submit"
            className="bg-black text-white hover:bg-gray-800 px-12 py-5 text-sm"
            disabled={isSubmitting || customerLoading || paymentLoading}
          >
            {(isSubmitting || customerLoading || paymentLoading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Place Order
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CheckoutForm;


